import React, { useState, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Formik, ErrorMessage, useFormikContext } from 'formik'
import * as Yup from 'yup'
import { addDays, addYears, isAfter } from 'date-fns'
import { isEmpty, uniq } from 'lodash'

import Api from 'services/Api'
import BaseModal from 'components/Modals/BaseModal'
import {
  Button,
  Checkbox,
  DateTimePicker,
  MultiSelect,
  Textarea,
  Select,
  SelectScheduleType,
  WagePremiums,
  BillingDetails,
  Attributes,
  FormRow,
  Form,
  FileAttachments
} from 'components/Forms'
import {
  AssignedUserFlags,
  AutoRecalculateBreaks,
  Breaks,
  ConfirmationStatus,
  IsShiftOver16Hours,
  ShiftHistory,
  ShiftMultiCreation,
  UserInputWithFlags,
  RecurrenceRule
} from 'components/Schedule'
import { UPLOAD_ATTACHMENTS_TYPES } from 'common'

import { Loading, Collapsable, Tabs } from 'components/UI'
import { Format, Parse, setTime, localizedStartOfWeek } from 'services/Dates'
import { getProjectsForMultiselect } from 'services/getProjectsForMultiselect'
import { convertToRuleObject, convertFromRuleString } from 'services/schedule/RecurrenceRules'
import { getBreaksToApply, getStartEndFromTimeDate, getScheduleTypeInitialvalue, getStartEnd } from 'services/schedule'
import { getAssignablePositions } from 'services/Structure'
import { getParsedTagsForMultiselect } from 'services/getParsedTagsForMultiselect'
import { closeModal } from 'store/actions/modals'
import { createEvents } from 'store/actions/events'
import { createErrorNotification } from 'store/actions/notifications'
import { TrackAction } from 'services/TrackAction'
import { hasAccessToThis, flattenObject } from 'services/Utilities'
import { Alert } from 'ui'
import { Card } from 'ui'

import { t } from 'translations'

const ScheduleEditModal = ({ _id, employeeId = '', end, isWeekly, onModalClose, positionId, projectId, teamId, start }) => {
  const dispatch = useDispatch()
  const { _id: userId } = useSelector(state => state.users.userInfo)
  const { projects, projectsById, teams, procedures, usersById } = useSelector(state => state.resources)
  const proceduresOptions = useMemo(() => procedures.filter(({ includeInSchedule }) => includeInSchedule), [procedures])
  const {
    defaultTimeZone: timeZone,
    enableShiftSpecificAttributes,
    activateWorkdaySchedule,
    activateWorkdayIntegration,
    enableReccurence,
    enableBillingDetails,
    projectIsRequired,
    teamIsRequired,
    ignoreReportsToForProjects,
    breaksAreRequired
  } = useSelector(state => state.settings)
  const [event, setEvent] = useState()
  const [eventDeleted, setEventDeleted] = useState(false)
  const [validFlags, setValidFlags] = useState([])
  const showOtherInfo = projectIsRequired || teamIsRequired || breaksAreRequired

  const shiftStartDate = Parse(start ?? event?.startDate, { inFormat: 'date' })

  useEffect(() => {
    const fetchOldEvent = async () => {
      const { result, success } = await Api.get('schedule/schedule', { params: { _id, useNewReturn: true } })
      if (!success || !result.length) return setEventDeleted(true)
      const { start, end, rrule, ...rest } = result[0]

      setEvent({
        ...rest,
        startDate: Format(start, { outFormat: 'date' }),
        start: Format(start, { outFormat: 'serverTime' }),
        end: Format(end, { outFormat: 'serverTime' }),
        rrule: convertFromRuleString(rrule, { defaultUntil: Format(start, { outFormat: 'date' }) })
      })
    }

    if (_id !== 'new') fetchOldEvent()
    else {
      const startEnd = {
        start: Format(start, { outFormat: 'serverTime' }),
        end: Format(end, { outFormat: 'serverTime' }),
        // Get start/end will check if there is a schedule type, default to select start end if there isn't
        ...getStartEnd(undefined, {
          _id,
          start: Format(start, { outFormat: 'serverTime' }),
          end: Format(end, { outFormat: 'serverTime' })
        })
      }
      setEvent({
        startDate: Format(start, { outFormat: 'date' }),
        positionId,
        projects: projectId ? [projectId] : [],
        team: teamId,
        // Add default shift tags from project if they exist
        tags: projectsById[projectId]?.defaultShiftTags?.length ? projectsById[projectId].defaultShiftTags : [],
        billingDetails: projectsById[projectId]?.billingDetails ? projectsById[projectId].billingDetails : { billingId: '' },
        breaks: getBreaksToApply(startEnd, positionId),
        rrule: convertFromRuleString('', { defaultUntil: Format(start, { outFormat: 'date' }) }),
        ...startEnd
      })
    }
  }, [_id, start, end, positionId, projectId, teamId, projectsById])

  const onSubmit = async ({
    changeReason,
    changeDescription,
    creationDescription,
    sendModificationCancellationEmails,
    sendNotificationForNewEmployee,
    updateFutureRecurrence,
    startDate,
    start,
    end,
    ...values
  }) => {
    let shiftId
    // Parse project field that is not an array in frontend
    values.projects = values.projects && values.projects.length ? [values.projects] : []

    // Format start and end date to server format
    const [parsedStart, parsedEnd] = getStartEndFromTimeDate({ startDate, start, end })
    values.start = parsedStart
    values.end = parsedEnd

    if (values.addToOtherDays) {
      let days = []
      for (const [key, value] of Object.entries(values.addToOtherDays.byWeekDay)) {
        if (value) {
          days.push(addDays(localizedStartOfWeek(Parse(values.start)), key))
        }
      }
      values.addToOtherDays = days.map(date => Format(date, { outFormat: 'date' }))
    } else delete values.addToOtherDays

    // Convert recurrence rule to string
    if (values.rrule && values.rrule.frequency !== 'NONE')
      values.rrule = convertToRuleObject({ ...values.rrule, dtstart: Parse(startDate, { inFormat: 'date' }) }).toString()
    else values.rrule = ''

    if (_id !== 'new') {
      // Allows user to select a reason for making the change to an existing event
      let options = {
        sendModificationEmails: sendModificationCancellationEmails && !sendNotificationForNewEmployee,
        sendCancellationEmails: sendModificationCancellationEmails,
        updateFutureRecurrence
      }
      if (changeReason !== 'NO_REASON') options.reason = { text: changeReason, description: changeDescription }

      const { result, success } = await Api.put('schedule/partial', { event: values, options })
      if (!success) return dispatch(createErrorNotification())
      shiftId = result[0]._id

      // Send a notification to the newly assigned employee
      if (sendNotificationForNewEmployee) {
        const body = {
          shiftIds: [values._id],
          options: { confirm: true, reconfirm: true, type: 'confirmation' }
        }
        await Api.post('schedule/confirm', body)
      }

      dispatch(createEvents(result))

      // Track that user has edited a shift
      TrackAction('EDIT_SHIFT')
    } else {
      const { result, success } = await Api.post('schedule/schedule', {
        event: values,
        options: { reason: { text: '', description: creationDescription } }
      })
      if (!success) return dispatch(createErrorNotification())
      shiftId = result[0]?._id

      // Add the shift to redux
      dispatch(createEvents(result))

      // Track that user has created a shift
      TrackAction('CREATE_SHIFT')
    }
    if (onModalClose) onModalClose(shiftId)
    dispatch(closeModal())
  }

  const breaksShape = Yup.array().of(
    Yup.object().shape({
      name: Yup.string(),
      duration: Yup.number(),
      paid: Yup.boolean().nullable()
    })
  )

  const filesShape = Yup.array().of(
    Yup.object().shape({
      name: Yup.string(),
      key: Yup.string(),
      type: Yup.string()
    })
  )

  let shiftShape = Yup.object().shape(
    {
      _id: Yup.string(),
      employeeId: Yup.string()
        .nullable()
        .test('user-archived', 'manager.schedule.USER_IS_ARCHIVED', value => {
          return !usersById[value]?.archived
        }),
      procedureId: Yup.string().nullable(),
      positionId: Yup.string().required('manager.schedule.REQUIRED_FIELD'),
      projects: projectIsRequired ? Yup.string().required('manager.schedule.REQUIRED_FIELD').nullable() : Yup.string().nullable(),
      team: teamIsRequired ? Yup.string().required('manager.schedule.REQUIRED_FIELD').nullable() : Yup.string().nullable(),
      start: Yup.string().required('manager.schedule.REQUIRED_FIELD'),
      end: Yup.string().required('manager.schedule.REQUIRED_FIELD'),
      comment: Yup.string(),
      isExtraShift: Yup.boolean().nullable(),
      isOvertime: Yup.boolean().nullable(),
      isOnCall: Yup.boolean().nullable(),
      isIrreplaceable: Yup.boolean().nullable(),
      needsReplacement: Yup.boolean().nullable(),
      coveredOtherUnit: Yup.boolean().nullable(),
      noEmployeeAvailable: Yup.boolean().nullable(),
      hasSpecificRequirements: Yup.boolean().nullable(),
      updateFutureRecurrence: Yup.boolean().nullable(),
      tags: Yup.array(Yup.string()),
      changeReason: Yup.string(),
      changeDescription: Yup.string(),
      duplicateNumber: Yup.number()
        .min(0)
        .max(100, t('manager.schedule.TOO_MUCH_IDENTICAL_SHIFT', { number: 100 }))
        .integer(),
      requiredAttributes: Yup.array(),
      selectedScheduleType: Yup.string(),
      rrule: Yup.object().shape({
        count: Yup.number()
          .min(0)
          .max(500, t('manager.schedule.TOO_MUCH_IDENTICAL_SHIFT', { number: 500 }))
          .integer(),
        until: Yup.string().test('test-date-until', 'manager.schedule.DATE_TOO_FAR', value => {
          return !isAfter(Parse(value, { inFormat: 'date' }), addYears(shiftStartDate, 2))
        })
      }),
      sendNotificationForNewEmployee: Yup.boolean(),
      sendModificationCancellationEmails: Yup.boolean(),
      breaks: breaksAreRequired ? breaksShape.min(1, t('manager.schedule.REQUIRED_FIELD')) : breaksShape,
      files: filesShape,
      wagePremiums: Yup.array().of(
        Yup.object().shape({
          fullShift: Yup.boolean(),
          hours: Yup.number().when('fullShift', {
            is: false,
            then: schema => schema.moreThan(0, 'administration.wagePremiums.form.GREATER_THAN_ZERO')
          }),
          unitBased: Yup.boolean(),
          units: Yup.number().when('unitBased', {
            is: true,
            then: schema => schema.moreThan(0, 'administration.wagePremiums.form.GREATER_THAN_ZERO')
          })
        })
      )
    },
    [['projects', 'team']]
  )

  // Conditionally enable positionId validation
  if (activateWorkdayIntegration && activateWorkdaySchedule) {
    shiftShape.positionId = Yup.string().when('projects', (projects, schema) => {
      let requiredPositions = projectsById[projects]?.positions || []
      return projects ? schema.oneOf(requiredPositions, 'manager.schedule.eventForm.ELIGIBLE_POSITION') : schema
    })
  }

  const EditForm = () => (
    <Formik
      enableReinitialize={true}
      // The [projects, team] array is used to avoid cyclical dependency errors.
      // Not sure entirely how it works, but it works.
      // https://dev.to/gabrielterriaga/how-to-validate-two-fields-that-depend-on-each-other-with-yup-1ccg
      validationSchema={shiftShape}
      initialValues={{
        employeeId,
        procedureId: proceduresOptions?.[0]?._id ?? '',
        positionId: '',
        comment: '',
        isExtraShift: false,
        isOvertime: false,
        isOnCall: false,
        isIrreplaceable: false,
        needsReplacement: false,
        coveredOtherUnit: false,
        noEmployeeAvailable: false,
        hasSpecificRequirements: false,
        updateFutureRecurrence: false,
        startTimezone: timeZone,
        endTimezone: timeZone,
        tags: [],
        wagePremiums: [],
        requiredAttributes: [],
        billingDetails: { billingId: '' },
        team: '',
        ...event,
        projects: Array.isArray(event.projects) && event.projects[0] ? event.projects[0] : '',
        changeReason: 'NO_REASON',
        changeDescription: '',
        selectedScheduleType: getScheduleTypeInitialvalue([event]),
        duplicateNumber: 1,
        sendModificationCancellationEmails: false,
        sendNotificationForNewEmployee: false,
        files: event.files ?? []
      }}
      onSubmit={values => onSubmit(values)}
    >
      {({ values, initialValues, status, isSubmitting, dirty, setStatus, setFieldValue }) => {
        // Detect if positionId, project,comment or start/end have changed
        const hasProjectChanged = values.projects !== initialValues.projects
        const hasPositionChanged = values.positionId !== initialValues.positionId
        const hasTimeChanged = values.start !== initialValues.start || values.end !== initialValues.end
        const hasCommentChanged = values.comment !== initialValues.comment
        const hasStartDateChanged = values.startDate !== initialValues.startDate
        const employeeHaschanged = values.employeeId !== initialValues.employeeId

        const importantChanges = hasProjectChanged || hasPositionChanged || hasTimeChanged || hasCommentChanged || hasStartDateChanged || employeeHaschanged

        return (
          <Form>
            {/* Error message */}
            <Alert variant={status?.type} onClose={() => setStatus()} show={!!status?.msg}>
              {status?.msg}
            </Alert>

            <IsShiftOver16Hours {...values} />

            {/* Form */}
            {/* Main info */}
            <UserInputWithFlags event={values} />
            {/* If there is only one procedure, it will be selected by default */}
            {proceduresOptions?.length > 1 ? <Select module="manager.schedule.EDIT_MODAL" name="procedureId" options={proceduresOptions} /> : null}
            {/* If there is no procedure that is included in the schedule, display a warning */}
            <Alert variant="warning" show={isEmpty(proceduresOptions)}>
              {t('manager.schedule.NO_PROCEDURE_WARNING')}
            </Alert>
            <Alert variant="warning" show={!!event?.contactListId}>
              {t('manager.schedule.SHIFT_IN_LIST_WARNING')}
            </Alert>

            <AssignedUserFlags event={values} setValidFlags={setValidFlags} />

            <MultiSelect
              module="manager.schedule.eventForm"
              name="positionId"
              required
              options={getAssignablePositions({ forMultiSelect: true, projectId: values.projects })}
            />
            {/* Date and time stuff */}
            <FormRow>
              <DateTimePicker module="manager.schedule.eventForm" name="start" required type="time" />
              <DateTimePicker module="manager.schedule.eventForm" name="end" required type="time" />
            </FormRow>
            <AutoRecalculateBreaks values={values} initialValues={initialValues} setFieldValue={setFieldValue} />
            <FormRow>
              <DateTimePicker module="manager.schedule.eventForm" name="startDate" type="date" required />

              {/* Radios for selecting shift type */}
              <SelectScheduleType shift={values} setFieldValue={setFieldValue} />
            </FormRow>

            <Textarea module="manager.schedule.eventForm" name="comment" isTranslatable />

            <Collapsable type="attachment" title={t('forms.fileUpload.ATTACHMENTS')} show={values.files.length > 0}>
              <FileAttachments
                onAfterDrop={file => setFieldValue('files', [...values.files, file])}
                onAfterRemove={file =>
                  setFieldValue(
                    'files',
                    values.files.filter(myfile => myfile.key !== file.key)
                  )
                }
                files={values.files}
                attachmentType={UPLOAD_ATTACHMENTS_TYPES.SHIFT}
                _id={_id}
              />
            </Collapsable>

            {/* Multi-creation */}
            {enableReccurence ? (
              <RecurrenceRule
                rrule={values.rrule}
                currentDate={setTime(Parse(values.startDate, { inFormat: 'date' }), values.start)}
                setFieldValue={setFieldValue}
              />
            ) : null}
            <ShiftMultiCreation
              isCreating={_id === 'new'}
              amountOfDay={isWeekly ? 7 : 1}
              currentDate={setTime(Parse(values.startDate, { inFormat: 'date' }), values.start)}
              rrule={values.rrule}
              addToOtherDays={values.addToOtherDays}
            />

            {/* Shift options */}
            <Collapsable type="shiftOptions" title="manager.schedule.eventForm.SHIFT_OPTIONS">
              <Checkbox module="manager.schedule.eventForm" name="isExtraShift" />
              <Checkbox module="manager.schedule.eventForm" name="isOvertime" />
              <Checkbox module="manager.schedule.eventForm" name="isOnCall" />
              <Checkbox module="manager.schedule.eventForm" name="isIrreplaceable" />
              <Checkbox module="manager.schedule.eventForm" name="needsReplacement" />
              <Checkbox module="manager.schedule.eventForm" name="noEmployeeAvailable" />
              <Checkbox module="manager.schedule.eventForm" name="hasSpecificRequirements" />
              <Checkbox module="manager.schedule.eventForm" name="coveredOtherUnit" />
            </Collapsable>

            {/* Extra info */}
            <Collapsable type="otherInfo" title="manager.schedule.eventForm.OTHER_INFO" show={showOtherInfo}>
              <MultiSelect
                module="manager.schedule.eventForm"
                name="projects"
                options={getProjectsForMultiselect(projects, {
                  ignoreReportsToForProjects,
                  permission: !!hasAccessToThis('limitToReportsTo'),
                  connectedUser: userId,
                  t
                })}
                handleChange={projectId => {
                  // Add default shift tags from project if relevant
                  if (projectsById[projectId]?.defaultShiftTags?.length) {
                    setFieldValue('tags', uniq(values.tags.concat(projectsById[projectId].defaultShiftTags)))
                  }

                  if (projectsById[projectId]?.billingDetails) {
                    setFieldValue('billingDetails', projectsById[projectId].billingDetails)
                  }
                }}
                isClearable
                // Value when the field is cleared
                defaultValue={''}
                required={projectIsRequired}
                isOptionDisabled={({ isDisabled }) => isDisabled}
              />

              <MultiSelect
                module="manager.schedule.eventForm"
                name="team"
                options={teams.map(({ _id: value, name: label, reportsTo = [] }) => {
                  const canAssignTeam = !hasAccessToThis('limitToReportsTo') || reportsTo.includes(userId)
                  return { label, value, isHidden: !canAssignTeam }
                })}
                required={teamIsRequired}
                isClearable
                // Value when the field is cleared
                defaultValue={''}
              />

              <MultiSelect module="manager.schedule.eventForm" name="tags" options={getParsedTagsForMultiselect()} isClearable isMulti />

              <Breaks
                event={{ start: values.start, end: values.end, positionId: values.positionId }}
                setFieldValue={(...args) => setFieldValue(...args)}
                breaks={values.breaks}
                allowRecalculate
                required={breaksAreRequired}
              />

              <WagePremiums wagePremiums={values.wagePremiums} />

              {enableShiftSpecificAttributes ? (
                <Attributes module="manager.schedule.eventForm" name="requiredAttributes" attributes={values.requiredAttributes} />
              ) : null}
            </Collapsable>
            {!!enableBillingDetails && (
              <Collapsable type="billingDetails" title="components.billingDetails.BILLING_DETAILS">
                <BillingDetails context="shift" />
              </Collapsable>
            )}

            {/* Reason for shift change */}
            {dirty && _id !== 'new' ? (
              <ChangeComponent employeeHaschanged={employeeHaschanged} importantChanges={importantChanges} event={event} validFlags={validFlags} />
            ) : null}

            {/* Reason for shift creation */}
            {_id === 'new' ? (
              <Card className="bg-light mb-2 p-3">
                <Textarea module="manager.schedule.eventForm" name="creationDescription" />
              </Card>
            ) : null}

            {/* Option to modify all future shifts of recurrence */}
            <Alert variant="warning" show={!!values.rruleId && !!enableReccurence}>
              <Checkbox module="manager.schedule.eventForm" name="updateFutureRecurrence" />
            </Alert>

            {/* Error message */}
            <ErrorMessage
              render={errors => {
                const flattenedErrors = flattenObject(errors)
                const validErrors = Object.keys(flattenObject(errors)).filter(error => typeof flattenedErrors[error] === 'string')
                // If no error, display nothing
                if (!Object.keys(validErrors).length) return null

                return (
                  <Alert variant="danger">
                    <ul>
                      {validErrors.map(error => {
                        const isObjectFieldError = error.includes('.')
                        return (
                          <li key={error}>
                            {isObjectFieldError ? (
                              <b>{t(`components.recurrenceRule.form.${error.split('.')[1].toUpperCase()}.LABEL`)}:</b>
                            ) : (
                              <b>{t(`manager.schedule.eventForm.${error.toUpperCase()}.LABEL`)}:</b>
                            )}
                            {t(flattenedErrors[error])}
                          </li>
                        )
                      })}
                    </ul>
                  </Alert>
                )
              }}
            />
            {/* Submit & Cancel buttons */}
            <Button className="btn btn-primary mr-2" type="submit" isSubmitting={isSubmitting} text="SUBMIT" />
            <Button className="btn btn-outline-secondary" onClick={() => dispatch(closeModal())}>
              {t(`actions.CLOSE`)}
            </Button>
          </Form>
        )
      }}
    </Formik>
  )

  const getModalContent = () => {
    if (!event && !eventDeleted) return <Loading />

    if (eventDeleted)
      return (
        <div className="pt-2 pb-3 px-3">
          <Alert variant="warning">{t('manager.schedule.EDIT_MODAL.EVENT_DELETED')}</Alert>
          <Button text="actions.CLOSE" className="btn btn-outline-secondary" onClick={() => dispatch(closeModal())} />
        </div>
      )

    if (event.finalized)
      return (
        <div className="pt-2 pb-3 px-3">
          <Alert variant="warning">{t('manager.schedule.EDIT_MODAL.EVENT_FINALIZED')}</Alert>
          <Button text="actions.CLOSE" className="btn btn-outline-secondary" onClick={() => dispatch(closeModal())} />
        </div>
      )

    if (_id === 'new') return <div className="pt-0 px-3 pb-3">{EditForm()}</div>

    return (
      <Tabs
        module="manager.schedule.EDIT_MODAL"
        tabs={[
          { name: 'shift', component: <div className="pt-2 px-3 pb-3">{EditForm()}</div> },
          {
            name: 'confirmation',
            component: (
              <div className="p-3">
                <ConfirmationStatus _id={_id} />
              </div>
            )
          },
          {
            name: 'history',
            component: (
              <div className="p-3">
                <ShiftHistory diffs={event.diffs} />
              </div>
            )
          }
        ]}
      />
    )
  }

  return (
    <BaseModal hideButtons noPadding title={`manager.schedule.${_id === 'new' ? 'CREATE_MODAL' : 'EDIT_MODAL'}`}>
      {getModalContent()}
    </BaseModal>
  )
}

const ChangeComponent = ({ employeeHaschanged, importantChanges, event, validFlags }) => {
  // Grab values from context
  const { values, setFieldValue } = useFormikContext()
  const { published } = event
  const shouldSendModificationCancellationEmails = importantChanges && published
  const shouldSendNotificationForNewEmployee = employeeHaschanged && published
  useEffect(() => {
    setFieldValue('sendModificationCancellationEmails', shouldSendModificationCancellationEmails)
    setFieldValue('sendNotificationForNewEmployee', shouldSendNotificationForNewEmployee)
  }, [setFieldValue, shouldSendModificationCancellationEmails, shouldSendNotificationForNewEmployee])

  return (
    <Card className="bg-light mb-2 p-3">
      <Select
        module="manager.schedule.eventForm"
        name="changeReason"
        options={[
          { _id: 'NO_REASON', name: t('manager.schedule.CHANGE_REASONS.NO_REASON') },
          { _id: 'CHANGED_HOURS', name: t('manager.schedule.CHANGE_REASONS.CHANGED_HOURS') },
          { _id: 'CHANGED_ASSIGNED_EMPLOYEE', name: t('manager.schedule.CHANGE_REASONS.CHANGED_ASSIGNED_EMPLOYEE') },
          ...validFlags.map(flag => ({ _id: flag, name: t(`manager.schedule.CHANGE_REASONS.${flag.toUpperCase()}`) })),
          { _id: 'OTHERS', name: t('manager.schedule.CHANGE_REASONS.OTHERS') }
        ]}
      />
      {values.changeReason !== 'NO_REASON' ? <Textarea module="manager.schedule.eventForm" name="changeDescription" /> : null}
      {/* Whether or not to send a modification email */}
      {published ? (
        <>
          {/* Send a cancellation/modification notice to the former employee*/}
          {importantChanges ? <Checkbox module="manager.schedule.eventForm" name="sendModificationCancellationEmails" /> : null}
          {/* Send a notice to the newly assigned employee */}
          {employeeHaschanged ? <Checkbox module="manager.schedule.eventForm" name="sendNotificationForNewEmployee" /> : null}
        </>
      ) : null}
    </Card>
  )
}

export default ScheduleEditModal
